import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import { createApiClient } from '../../../../api/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UploadError {
  rowNumber: number;
  columnLetter: string;
  columnName: string;
  value: string;
  message: string;
}

interface UploadResult {
  totalRows?: number;
  insertedCount?: number;
  errorCount?: number;
  hasErrors?: boolean;
  errors?: UploadError[];
  message?: string;
  // Legacy fields for backwards compatibility
  rowsInserted?: number;
  rowsProcessed?: number;
  rowsSkipped?: number;
  warnings?: string[];
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const BulkCsvUploadModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{
    type: 'success' | 'validation' | 'error';
    data: UploadResult;
  } | null>(null);
  const [apiError, setApiError] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<any>(null);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const resetState = () => {
    setFile(null);
    setFileError('');
    setResult(null);
    setApiError('');
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (isUploading) abortControllerRef.current?.abort();
    resetState();
    onClose();
  };

  const handleCloseSuccess = () => {
    resetState();
    onClose();
    onSuccess();
  };

  // ── File selection ─────────────────────────────────────────────────────────

  const triggerFileInput = () => fileInputRef.current?.click();

  const handleFileChange = (e: any) => {
    const selected: File | undefined = e.target.files?.[0];
    setFileError('');
    setResult(null);
    setApiError('');
    if (!selected) { setFile(null); return; }
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setFileError('Please select a valid CSV file (.csv extension required).');
      setFile(null);
      e.target.value = '';
      return;
    }
    if (selected.size === 0) {
      setFileError('The selected file is empty. Please choose a non-empty CSV file.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(selected);
  };

  const clearFile = (e?: any) => {
    e?.stopPropagation?.();
    setFile(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Upload ─────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file) { setFileError('Please select a CSV file first.'); return; }
    const form = new FormData();
    form.append('file', file);

    abortControllerRef.current = new AbortController();
    setIsUploading(true);
    setProgress(0);
    setResult(null);
    setApiError('');

    try {
      const api = createApiClient();
      const resp = await (api as any).post(
        '/perfect-truck/config/location-route-mapping/upload',
        form,
        {
          signal: abortControllerRef.current.signal,
          onUploadProgress: (evt: any) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        },
      );
      setResult({ type: 'success', data: resp.data });
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        setApiError('Upload was cancelled.');
      } else if (err?.response?.status === 422) {
        setResult({ type: 'validation', data: err.response.data });
      } else if (err?.response?.status === 400) {
        setApiError(err.response.data?.message ?? 'Invalid file or missing required columns. Please check the format and try again.');
      } else {
        setApiError(err?.response?.data?.message ?? err?.message ?? 'Upload failed. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // ── Error report download ──────────────────────────────────────────────────

  const downloadErrorReport = () => {
    if (!result?.data?.errors?.length || Platform.OS !== 'web') return;
    const lines = [
      'Row,Column,Column Name,Value,Error Message',
      ...result.data.errors.map(
        e => `${e.rowNumber},"${e.columnLetter}","${e.columnName.replace(/"/g, '""')}","${e.value.replace(/"/g, '""')}","${e.message.replace(/"/g, '""')}"`
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'route-upload-errors.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const isValidation = result?.type === 'validation';
  const isSuccess = result?.type === 'success';
  const errorCount = result?.data?.errors?.length ?? 0;

  const InputTag = 'input' as any;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminModal
      visible={visible}
      title="Bulk CSV Upload"
      onClose={handleClose}
      footer={
        <>
          <AdminButton
            label={isUploading ? 'Cancel Upload' : 'Close'}
            variant="secondary"
            onPress={isUploading ? () => abortControllerRef.current?.abort() : (isSuccess ? handleCloseSuccess : handleClose)}
          />
          {!isSuccess && (
            <AdminButton
              label={
                isUploading ? `Uploading ${progress}%…` :
                isValidation ? 'Try Again' :
                'Upload'
              }
              variant="primary"
              onPress={isValidation ? resetState : handleUpload}
              disabled={isUploading || (!isValidation && !file)}
            />
          )}
        </>
      }
    >
      {/* Format info banner */}
      <View style={styles.infoBanner}>
        <MaterialIcons name="info-outline" size={15} color={AdminColors.blue700} style={{ marginRight: 8, marginTop: 1 }} />
        <Text style={styles.infoText}>
          Upload a <Text style={styles.bold}>UTF-8 CSV</Text> with a header row.{' '}Mandatory columns (A–J):{' '}
          <Text style={styles.bold}>
            Location_ID, Location_Name, Route_Number, Display_Order, Location_Type, Organization_Type, Is_3PL, Is_Active, Route_Type
          </Text>
          {'. '}Optional columns (I, K–N):{' '}
          <Text style={styles.bold}>SAP_ID, Created_At, Created_By, Updated_At, Updated_By</Text>
          {'. '}Organization_Type must be <Text style={styles.bold}>PBNA</Text>, <Text style={styles.bold}>FLNA</Text>, or <Text style={styles.bold}>1NA</Text>.
        </Text>
      </View>

      {/* File picker (web only) */}
      {Platform.OS === 'web' && (
        <>
          <InputTag
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <TouchableOpacity
            style={[styles.filePickerBtn, !!file && styles.filePickerBtnSelected]}
            onPress={triggerFileInput}
            activeOpacity={0.7}
          >
            <MaterialIcons
              name={file ? 'insert-drive-file' : 'upload-file'}
              size={18}
              color={file ? AdminColors.blue700 : AdminColors.textMuted}
            />
            <Text
              style={[styles.filePickerText, !!file && styles.filePickerTextSelected]}
              numberOfLines={1}
            >
              {file ? file.name : 'Click to choose a CSV file…'}
            </Text>
            {!!file && (
              <TouchableOpacity onPress={clearFile} activeOpacity={0.7} style={{ marginLeft: 4 }}>
                <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
              </TouchableOpacity>
            )}
          </TouchableOpacity>
        </>
      )}
      {!!fileError && <Text style={styles.fieldError}>{fileError}</Text>}

      {/* Upload progress */}
      {isUploading && (
        <View style={styles.progressSection}>
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${progress}%` as any }]} />
          </View>
          <Text style={styles.progressLabel}>{progress}% uploaded…</Text>
        </View>
      )}

      {/* Network / server error */}
      {!!apiError && (
        <View style={styles.errorBanner}>
          <MaterialIcons name="error-outline" size={15} color={AdminColors.red500} style={{ marginRight: 6, marginTop: 1 }} />
          <View style={{ flex: 1 }}>
            <Text style={styles.errorText}>{apiError}</Text>
            {!isUploading && (
              <TouchableOpacity onPress={handleUpload} activeOpacity={0.7} style={{ marginTop: 4 }}>
                <Text style={styles.retryLink}>Retry upload →</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* Success */}
      {isSuccess && (
        <View style={styles.successBanner}>
          <MaterialIcons name="check-circle" size={15} color={AdminColors.green600} style={{ marginRight: 6, marginTop: 1 }} />
          <Text style={styles.successText}>
            Upload successful!{' '}
            <Text style={styles.bold}>
              {result!.data.insertedCount ?? result!.data.rowsInserted ?? result!.data.rowsProcessed ?? 0} row
              {(result!.data.insertedCount ?? result!.data.rowsInserted ?? 0) !== 1 ? 's' : ''} inserted
            </Text>
            {result!.data.totalRows ? ` out of ${result!.data.totalRows}` : ''}
            {result!.data.message ? `.\n${result!.data.message}` : '.'}
          </Text>
        </View>
      )}

      {/* Validation errors (422) */}
      {isValidation && (
        <View style={styles.validationSection}>
          <View style={styles.validationHeaderRow}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <MaterialIcons name="warning" size={15} color="#D97706" style={{ marginRight: 6 }} />
              <Text style={styles.validationTitle}>
                {errorCount} validation error{errorCount !== 1 ? 's' : ''} found
              </Text>
            </View>
            {errorCount > 0 && (
              <TouchableOpacity onPress={downloadErrorReport} activeOpacity={0.7}>
                <Text style={styles.downloadLink}>↓ Download Error Report</Text>
              </TouchableOpacity>
            )}
          </View>

          {errorCount > 0 && (
            <ScrollView style={styles.errorTableScroll} nestedScrollEnabled>
              <View style={styles.errorTableHeader}>
                <Text style={[styles.errorTh, { flex: 0.4 }]}>Row</Text>
                <Text style={[styles.errorTh, { flex: 0.6 }]}>Col</Text>
                <Text style={[styles.errorTh, { flex: 1.2 }]}>Field</Text>
                <Text style={[styles.errorTh, { flex: 1.8 }]}>Error</Text>
              </View>
              {(result!.data.errors ?? []).slice(0, 20).map((e, i) => (
                <View key={i} style={[styles.errorTableRow, i % 2 === 0 && styles.errorTableRowAlt]}>
                  <Text style={[styles.errorTd, { flex: 0.4 }]}>{e.rowNumber}</Text>
                  <Text style={[styles.errorTd, { flex: 0.6 }]}>{e.columnLetter}</Text>
                  <Text style={[styles.errorTd, { flex: 1.2 }]} numberOfLines={2}>{e.columnName}</Text>
                  <View style={{ flex: 1.8 }}>
                    <Text style={[styles.errorTd, styles.errorMessage]} numberOfLines={3}>{e.message}</Text>
                    {e.value && <Text style={styles.errorValue} numberOfLines={1}>Value: {e.value}</Text>}
                  </View>
                </View>
              ))}
              {errorCount > 20 && (
                <Text style={styles.moreErrors}>
                  …and {errorCount - 20} more. Download the error report for the full list.
                </Text>
              )}
            </ScrollView>
          )}

          <Text style={styles.retryHint}>
            Fix the errors in your CSV and click "Try Again" to re-upload.
          </Text>
        </View>
      )}
    </AdminModal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.blue50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    padding: 12,
    marginBottom: 14,
  },
  infoText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textPrimary,
    lineHeight: 18,
  },
  bold: {
    fontFamily: FontFamily.bold,
  },

  // ── File picker
  filePickerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    paddingHorizontal: 14,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    backgroundColor: AdminColors.bgPage,
    marginBottom: 6,
    gap: 8,
    ...Platform.select({
      web: { borderStyle: 'dashed', cursor: 'pointer' } as any,
      default: {},
    }),
  },
  filePickerBtnSelected: {
    borderColor: AdminColors.blue700,
    backgroundColor: AdminColors.blue50,
    ...Platform.select({ web: { borderStyle: 'solid' } as any, default: {} }),
  },
  filePickerText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textMuted,
  },
  filePickerTextSelected: {
    color: AdminColors.textPrimary,
  },
  fieldError: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.red500,
    marginBottom: 8,
  },

  // ── Progress
  progressSection: {
    marginTop: 8,
    marginBottom: 4,
  },
  progressTrack: {
    height: 6,
    backgroundColor: AdminColors.borderDefault,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: 6,
    backgroundColor: AdminColors.blue700,
    borderRadius: 3,
  },
  progressLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    marginTop: 4,
    textAlign: 'center',
  },

  // ── Error banner
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.red500,
    padding: 10,
    marginTop: 8,
  },
  errorText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.red500,
    lineHeight: 20,
  },
  retryLink: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.red500,
    textDecorationLine: 'underline',
  },

  // ── Success banner
  successBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.green75,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.green600,
    padding: 10,
    marginTop: 8,
  },
  successText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.green600,
    lineHeight: 20,
  },

  // ── Validation errors
  validationSection: {
    marginTop: 12,
  },
  validationHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  validationTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: '#D97706',
  },
  downloadLink: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue700,
    textDecorationLine: 'underline',
  },
  errorTableScroll: {
    maxHeight: 200,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    marginBottom: 8,
  },
  errorTableHeader: {
    flexDirection: 'row',
    backgroundColor: AdminColors.blue50,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  errorTh: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.gray450,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  errorTableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderTable,
  },
  errorTableRowAlt: {
    backgroundColor: '#F5F9FF',
  },
  errorTd: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textPrimary,
    lineHeight: 16,
  },
  errorMessage: {
    color: AdminColors.red700,
    fontFamily: FontFamily.medium,
    marginBottom: 2,
  },
  errorValue: {
    fontFamily: FontFamily.regular,
    fontSize: 10,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
  },
  moreErrors: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    padding: 8,
    textAlign: 'center',
  },
  retryHint: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontStyle: 'italic',
  },
});

export default BulkCsvUploadModal;
